import React, { useEffect, useState, useContext, useMemo } from "react";
import axiosInstance from "api/axiosInstance";
import AutoRenewToggle from "components/mas/AutoRenewToggle";
import { toast } from "react-toastify";
import {
  Card,
  Typography,
  Grid,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import PlanCompareTable from "./PlanCompareTable";
import Tabs from "components/mas/Tabs";
import { LanguageContext } from "../../../../context/LanguageContext";
import { trackEvent } from "utils/analytics";
import { COLORS, TYPO } from "theme/tokens";

// ─────────────────────────────────────────────────────────────
// 共用對照表
// ─────────────────────────────────────────────────────────────
const PLAN_LEVEL = {
  FREE: 0,
  STARTER: 1,
  GROWTH: 2,
  PRO: 3,
  FREE_TRIAL: 1,
  BRONZE: 2,
  SILVER: 3,
  GOLD: 3,
};

// 顯示「目前方案 / 當前身份」時用：去掉方案名裡的（中文說明）/(英文說明) 括號，只留主名
//（下方「選擇 / 介紹方案」的列表卡片仍用含括號的完整名）
const stripPlanSuffix = (name) => (name || "").replace(/（[^）]*）/g, "").replace(/\([^)]*\)/g, "").trim();

const PLAN_NAME_KEY = {
  FREE: "Content43",
  STARTER: "Content37",
  GROWTH: "Content38",
  PRO: "Content39",
  FREE_TRIAL: "Content28",
  BRONZE: "Content29",
  SILVER: "Content30",
  GOLD: "Content31",
};

const PLAN_DESC_KEY = {
  FREE: "Content44",
  STARTER: "Content40",
  GROWTH: "Content41",
  PRO: "Content42",
  FREE_TRIAL: "Content33",
  BRONZE: "Content34",
  SILVER: "Content35",
  GOLD: "Content36",
};

// 各方案 tag 樣式（顏色 + 名稱呈現方式）
const PLAN_TAG_STYLE = {
  STARTER: { bg: COLORS.black, color: COLORS.white },      // 黑底白字「30 天試用中」
  FREE: { bg: COLORS.grayDark, color: COLORS.white },      // 灰底白字「Free」
  GROWTH: { bg: COLORS.main, color: COLORS.white },    // 橘底白字
  PRO: { bg: COLORS.purple, color: COLORS.white },       // 紫底白字
  FREE_TRIAL: { bg: COLORS.black, color: COLORS.white },
  BRONZE: { bg: COLORS.main, color: COLORS.white },
  SILVER: { bg: COLORS.grayDark, color: COLORS.white },
  GOLD: { bg: COLORS.purple, color: COLORS.white },
};

// ─────────────────────────────────────────────────────────────
// 子元件：目前方案卡片
// ─────────────────────────────────────────────────────────────
function CurrentPlanCard({ myPlan, L, onAutoRenewChanged }) {
  const code = myPlan?.planCode;
  if (!code) return null;

  const tagStyle = PLAN_TAG_STYLE[code] || PLAN_TAG_STYLE.FREE;
  const planName = L[PLAN_NAME_KEY[code]] || code;
  // 顯示「目前方案」時用簡潔名：去掉名稱裡的（中文說明）/(英文說明) 括號
  //（下方「選擇方案」的介紹卡片仍用含括號的完整名）
  const shortPlanName = stripPlanSuffix(planName);
  // TRIAL 顯示試用標籤、其他方案顯示簡潔方案名
  const tagLabel = code === "STARTER" || code === "FREE_TRIAL" ? stripPlanSuffix(L.TagTrialActive || "Starter Plan") : shortPlanName;

  // 收費方式
  let chargingText = "";
  if (code === "STARTER" || code === "FREE_TRIAL") {
    chargingText = `$0 ${L.ChargingPer30Days || "/ 15 天"}`;
  } else if (code === "FREE") {
    chargingText = `$0`;
  } else {
    // 月繳/年繳依後端 billingCycle 顯示（年繳是一次性付款、無 monthlyPrice，金額照舊顯示 —）
    const cycle = myPlan.billingCycle === "yearly" ? (L.BillingYearly || "年繳") : (L.BillingMonthly || "月繳");
    chargingText = `$${myPlan.monthlyPrice ?? "—"} / ${cycle}`;
  }

  return (
    <Card sx={{ p: 2.5, borderRadius: 3, boxShadow: 1, height: "100%", width: "100%" }}>
      {/* 標題列 */}
      <Box display="flex" alignItems="center" gap={1} mb={1.5}>
        <Box sx={{ width: 4, height: 16, bgcolor: COLORS.main, borderRadius: 1 }} />
        <MDTypography variant="subtitle2" fontWeight="bold">
          {L.CurrentPlanLabel || "目前方案"}
        </MDTypography>
      </Box>

      {/* Plan tag + 已到期 — 對齊截圖：tag 較大、字粗 */}
      <Box display="flex" alignItems="center" gap={1} mb={1.5}>
        <Box
          sx={{
            display: "inline-block",
            bgcolor: tagStyle.bg,
            color: tagStyle.color,
            px: 2.5,
            py: 0.75,
            borderRadius: 1,
            fontSize: "1rem",
            fontWeight: 700,
          }}
        >
          {tagLabel}
        </Box>
        {myPlan?.isExpired && (
          <Box
            sx={{
              border: "1px solid #f44336",
              color: COLORS.error,
              fontSize: "0.75rem",
              px: 1,
              py: 0.25,
              borderRadius: 0.5,
            }}
          >
            {L.Expired || "已到期"}
          </Box>
        )}
      </Box>

      {/* 目前方案 */}
      <Box display="flex" alignItems="center" gap={0.75} mb={0.75}>
        <PersonOutlineIcon sx={{ fontSize: 16, color: COLORS.grayDark }} />
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.85rem" }}>
          {L.CurrentPlanLabel || "目前方案"}：{shortPlanName}
        </Typography>
      </Box>

      {/* 收費方式 */}
      <Box display="flex" alignItems="center" gap={0.75} mb={1.5}>
        <AccountBalanceWalletIcon sx={{ fontSize: 16, color: COLORS.grayDark }} />
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.85rem" }}>
          {L.ChargingMethod || "收費方式："}{chargingText}
        </Typography>
      </Box>

      {/* 自動續費 toggle（共用元件 <AutoRenewToggle>，方案管理 + 帳務管理一致）*/}
      <AutoRenewToggle myPlan={myPlan} onChanged={onAutoRenewChanged} />
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────
// 子元件：方案時間卡片
// ─────────────────────────────────────────────────────────────
function PlanTimeCard({ myPlan, L }) {
  if (!myPlan) return null;

  const code = myPlan.planCode;
  // 顯示天數：TRIAL 用 trialRemainingDays；付費方案用訂閱剩餘（如有）；FREE 顯示 0
  let remainingDays = 0;
  if (code === "STARTER" || code === "FREE_TRIAL") {
    remainingDays = myPlan.trialRemainingDays ?? 0;
  } else if (myPlan.effectiveUntil) {
    // 月繳=nextBillingDate / 年繳=一次性交易到期日（後端 effectiveUntil 統一算好）。ceil 顯示完整天數
    const diff = Math.ceil((new Date(myPlan.effectiveUntil) - new Date()) / 86400000);
    remainingDays = diff > 0 ? diff : 0;
  }

  // 加入日期
  const joined = myPlan.joinedAt ? new Date(myPlan.joinedAt) : null;
  const joinedStr = joined
    ? `${joined.getFullYear()}.${String(joined.getMonth() + 1).padStart(2, "0")}.${String(joined.getDate()).padStart(2, "0")}`
    : "—";

  return (
    <Card sx={{ p: 2.5, borderRadius: 3, boxShadow: 1, height: "100%", width: "100%" }}>
      <Box display="flex" alignItems="center" gap={1} mb={1.5}>
        <Box sx={{ width: 4, height: 16, bgcolor: COLORS.main, borderRadius: 1 }} />
        <MDTypography variant="subtitle2" fontWeight="bold">
          {L.PlanTimeLabel || "方案時間"}
        </MDTypography>
      </Box>

      {/* 灰底容器較緊湊（py:2 + h4 對齊截圖） */}
      <Box
        sx={{
          bgcolor: COLORS.grayLightest,
          borderRadius: 2,
          py: 2,
          px: 2,
          textAlign: "center",
        }}
      >
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.8rem", display: "block", mb: 0.5 }}>
          {L.TimeRemaining || "有效剩餘時間"}
        </Typography>
        <Typography
          variant="h4"
          sx={{ color: COLORS.main, fontWeight: 700, lineHeight: 1.1, mb: 0.5 }}
        >
          {remainingDays} {L.Days || "天"}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.75rem" }}>
          {L.JoinedAt || "加入日期："}{joinedStr} {L.JoinedAtSuffix ?? "起"}
        </Typography>
      </Box>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────
// 子元件：升級方案卡片（橘黃漸層、月/年切換）
// ─────────────────────────────────────────────────────────────
function UpgradePlanCard({ plan, myPlan, billingPeriod, L, onClickActivate }) {
  const code = plan.code;
  const isCurrent = myPlan?.planCode === code;
  const isMostPopular = code === "PRO";
  const isPurchasable = !["FREE", "FREE_TRIAL", "STARTER"].includes(code);

  const tagStyle = PLAN_TAG_STYLE[code] || PLAN_TAG_STYLE.FREE;
  const planName = L[PLAN_NAME_KEY[code]] || plan.name || code;
  const planDesc = L[PLAN_DESC_KEY[code]] || plan.description || "";

  // 價格區塊
  let priceMain = "";
  let priceUnit = "";
  let strikethrough = "";
  if (code === "STARTER" || code === "FREE_TRIAL") {
    priceMain = "US$0";
    priceUnit = `/ 15 ${L.Days || "天"}`;
  } else if (code === "FREE") {
    priceMain = "US$0";
    priceUnit = "";
  } else if (billingPeriod === "yearly") {
    priceMain = `US$${plan.yearlyPrice ?? 0}`;
    priceUnit = `/ ${L.BillingYearly || "年"}`;
    // 標出原價（12 × 月費）
    const origYearly = (plan.monthlyPrice || 0) * 12;
    if (origYearly > plan.yearlyPrice) {
      strikethrough = `$${origYearly.toFixed(2)}`;
    }
  } else {
    priceMain = `US$${plan.monthlyPrice ?? 0}`;
    priceUnit = `/ ${L.BillingMonthly || "月"}`;
  }

  // 按鈕：currently using 變淡色 disabled；可升級顯示橘色
  const buttonDisabled = isCurrent || !isPurchasable;

  return (
    <Grid item xs={12} sm={6} md={4}>
      <Card
        sx={{
          position: "relative",
          width: "100%",
          height: "100%",
          p: 3,
          borderRadius: 3,
          boxShadow: isCurrent ? 4 : 1,
          // 邊框優先序：最受歡迎（PRO）橘框 > 目前使用方案 黃框 > 一般灰框
          border: isMostPopular
            ? "2px solid #f88e2b"
            : isCurrent
            ? "2px solid #fbc02d"
            : "1px solid #eee",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* 您目前使用 chip */}
        {isCurrent && (
          <Box
            sx={{
              position: "absolute",
              top: 12,
              left: 12,
              bgcolor: COLORS.safe,
              color: COLORS.white,
              fontSize: "0.7rem",
              fontWeight: 600,
              px: 1,
              py: 0.25,
              borderRadius: 0.5,
            }}
          >
            ✓ {L.CardCurrentlyUsing || "您目前使用"}
          </Box>
        )}

        {/* 最受歡迎 chip */}
        {isMostPopular && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              right: 0,
              background: "linear-gradient(90deg, #f88e2b 0%, #ffd166 100%)",
              color: COLORS.white,
              fontSize: "0.7rem",
              fontWeight: 700,
              px: 1.5,
              py: 0.4,
              borderTopRightRadius: 12,
              borderBottomLeftRadius: 8,
            }}
          >
            🔥 {L.CardMostPopular || "最受歡迎"}
          </Box>
        )}

        {/* Tag（plan name）— 2026-06-08 mt 統一給所有卡，保留 chip 區高度，三張卡內容才會對齊 */}
        <Box mt={3} mb={1.5}>
          <Box
            sx={{
              display: "inline-block",
              bgcolor: tagStyle.bg,
              color: tagStyle.color,
              px: 2,
              py: 0.5,
              borderRadius: 1,
              ...TYPO.title,
            }}
          >
            {code === "STARTER" || code === "FREE_TRIAL"
              ? (L.TagTrialActive || "Starter Plan（體驗版）")
              : planName}
          </Box>
        </Box>

        {/* 價格 */}
        <Box mb={0.5}>
          <Box display="flex" alignItems="baseline" gap={1}>
            <Typography sx={{ ...TYPO.displayL, color: COLORS.black }}>
              {priceMain}
            </Typography>
            <Typography sx={{ ...TYPO.bodyM, color: COLORS.grayDark }}>
              {priceUnit}
            </Typography>
            {strikethrough && (
              <Typography
                sx={{ ...TYPO.bodyM, color: COLORS.error, textDecoration: "line-through", ml: "auto" }}
              >
                {strikethrough}
              </Typography>
            )}
          </Box>
        </Box>

        {/* 描述（提示詞）*/}
        <Typography sx={{ ...TYPO.title, color: COLORS.grayDark }} mb={2}>
          {planDesc}
        </Typography>

        {/* feature list — label 可折行(英文標籤較長，取代原本截斷成「…」)，數值維持單行靠右 */}
        <Box
          sx={{
            flex: 1,
            "& .feature-row": {
              display: "flex",
              alignItems: "flex-start",
              gap: 1,
              py: 0.5,
            },
            "& .feature-row svg": {
              flexShrink: 0,
              mt: "2px",
            },
            "& .feature-row .label": {
              flex: 1,
              minWidth: 0,
              whiteSpace: "normal",
              wordBreak: "break-word",
            },
            "& .feature-row > :last-child": {
              flexShrink: 0,
              whiteSpace: "nowrap",
            },
          }}
        >
          <Box className="feature-row">
            <CheckCircleIcon sx={{ fontSize: 16, color: COLORS.main }} />
            <Typography sx={{ ...TYPO.bodyM, color: COLORS.black }} className="label">
              {L.CardFeatGen || "AI 策略助理-生成"}
            </Typography>
            <Typography sx={{ ...TYPO.bodyM, fontWeight: 500, color: COLORS.black }}>
              {(L.CardPerDay || "每日 {n} 次").replace("{n}", plan.strategyDailyLimit ?? 0)}
            </Typography>
          </Box>
          <Box className="feature-row">
            <CheckCircleIcon sx={{ fontSize: 16, color: COLORS.main }} />
            <Typography sx={{ ...TYPO.bodyM, color: COLORS.black }} className="label">
              {L.CardFeatAnalyze || "AI 策略助理-分析"}
            </Typography>
            <Typography sx={{ ...TYPO.bodyM, fontWeight: 500, color: COLORS.black }}>
              {(L.CardPerDay || "每日 {n} 次").replace("{n}", plan.backtestDailyLimit ?? 0)}
            </Typography>
          </Box>
          <Box className="feature-row">
            <CheckCircleIcon sx={{ fontSize: 16, color: COLORS.main }} />
            <Typography sx={{ ...TYPO.bodyM, color: COLORS.black }} className="label">
              {L.CardFeatSave || "策略儲存上限"}
            </Typography>
            <Typography sx={{ ...TYPO.bodyM, fontWeight: 500, color: COLORS.black }}>
              {(L.CardCount || "{n} 個").replace("{n}", plan.strategySaveLimit ?? 0)}
            </Typography>
          </Box>
          <Box className="feature-row">
            <CheckCircleIcon sx={{ fontSize: 16, color: COLORS.main }} />
            <Typography sx={{ ...TYPO.bodyM, color: COLORS.black }} className="label">
              {L.CardFeatWizGen || "策略精靈-生成策略"}
            </Typography>
            <Typography sx={{ ...TYPO.bodyM, fontWeight: 500, color: COLORS.black }}>
              {(L.CardPerMonth || "{n} 次 / 月").replace("{n}", plan.wizardMonthlyLimit ?? 0)}
            </Typography>
          </Box>
          <Box className="feature-row">
            <CheckCircleIcon sx={{ fontSize: 16, color: COLORS.main }} />
            <Typography sx={{ ...TYPO.bodyM, color: COLORS.black }} className="label">
              {L.CardFeatWizExec || "策略精靈-執行數量"}
            </Typography>
            <Typography sx={{ ...TYPO.bodyM, fontWeight: 500, color: COLORS.black }}>
              {plan.wizardExecLimit < 0
                ? (L.ValueUnlimited || "無上限")
                : plan.wizardExecLimit === 0
                ? (L.ValueCannotExec || "無法執行")
                : (L.CardCount || "{n} 個").replace("{n}", plan.wizardExecLimit)}
            </Typography>
          </Box>
        </Box>

        {/* CTA 按鈕 */}
        <MDButton
          fullWidth
          size="large"
          disabled={buttonDisabled}
          onClick={() => onClickActivate(plan)}
          sx={{
            mt: 2,
            borderRadius: 2,
            background: buttonDisabled
              ? COLORS.lighter
              : "linear-gradient(90deg, #f88e2b 0%, #ffd166 100%)",
            color: COLORS.white,
            "&:hover": {
              background: buttonDisabled
                ? COLORS.lighter
                : "linear-gradient(90deg, #f88e2b 0%, #ffd166 100%)",
            },
            "&:disabled": {
              background: COLORS.lighter,
              color: COLORS.white,
            },
          }}
        >
          {L.ActionActivate || "立即啟用"}
        </MDButton>
      </Card>
    </Grid>
  );
}

// ─────────────────────────────────────────────────────────────
// 主元件
// ─────────────────────────────────────────────────────────────
export default function PlanUpgradeManager() {
  const [plans, setPlans] = useState([]);
  const [myPlan, setMyPlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingPlan, setPendingPlan] = useState(null);
  const [pendingMethod, setPendingMethod] = useState(null);
  const [billingPeriod, setBillingPeriod] = useState("monthly"); // monthly | yearly

  const { componentLanguageData, loadLanguageFile, language } = useContext(LanguageContext);
  useEffect(() => {
    loadLanguageFile("PlanUpgradeManager", "plan/PlanUpgradeManager/PlanUpgradeManager_lang");
  }, [language]);
  const LANGUAGE = useMemo(
    () => componentLanguageData.PlanUpgradeManager || { Main: {}, Error: {}, Alert: {} },
    [componentLanguageData]
  );
  const L = LANGUAGE.Main || {};

  const fetchPlans = async () => {
    try {
      const res = await axiosInstance.get("/plans");
      const sorted = [...res.data].sort((a, b) => PLAN_LEVEL[a.code] - PLAN_LEVEL[b.code]);
      setPlans(sorted);
    } catch (err) {
      console.error("取得方案失敗", err);
    }
  };

  const fetchMyPlan = async () => {
    try {
      const res = await axiosInstance.get("/plans/me/plan");
      setMyPlan(res.data);
    } catch (err) {
      console.error("取得使用者方案失敗", err);
    }
  };

  useEffect(() => {
    fetchPlans();
    fetchMyPlan();
  }, []);

  // 升級流程：點「立即啟用」→ 開確認 dialog
  const handleClickActivate = (plan) => {
    const method = billingPeriod === "yearly" ? "purchase" : "subscribe";
    setPendingPlan(plan);
    setPendingMethod(method);
    setConfirmOpen(true);
  };

  const proceedUpgrade = async (planCode, method) => {
    setLoading(true);
    try {
      const myLevel = PLAN_LEVEL[myPlan?.planCode] || 0;
      const targetLevel = PLAN_LEVEL[planCode] || 0;

      if (targetLevel <= myLevel) {
        toast.error(LANGUAGE.Alert?.Content1 || "不可向下升級");
        return;
      }

      if (myLevel <= 1) {
        const endpoint = method === "subscribe" ? "/plans/subscribe" : "/plans/purchase";
        trackEvent("begin_checkout", { plan: planCode, method, billing_cycle: billingPeriod, value: billingPeriod === "yearly" ? pendingPlan?.yearlyPrice : pendingPlan?.monthlyPrice, currency: "USD" });
        const res = await axiosInstance.post(endpoint, { planCode });
        const popup = window.open();
        popup.document.write(res.data);
      } else {
        const isYearly = !myPlan?.hasActiveSubscription;
        if (isYearly) {
          const res = await axiosInstance.post("/plans/upgrade-plan", { planCode, method: "extend" });
          if (res.data?.success) {
            toast.success(LANGUAGE.Alert?.Content2 || "升級成功");
            fetchMyPlan();
          } else {
            toast.error(LANGUAGE.Alert?.Content3 || "升級失敗");
          }
        } else {
          const endpoint = method === "subscribe" ? "/plans/upgrade-subscribe" : "/plans/upgrade-pay";
          trackEvent("begin_checkout", { plan: planCode, method, billing_cycle: billingPeriod, value: billingPeriod === "yearly" ? pendingPlan?.yearlyPrice : pendingPlan?.monthlyPrice, currency: "USD" });
          const res = await axiosInstance.post(endpoint, { planCode });
          const popup = window.open();
          popup.document.write(res.data);
        }
      }
    } catch (err) {
      console.error("升級錯誤", err);
      toast.error(LANGUAGE.Alert?.Content4 || "升級過程發生錯誤");
    } finally {
      setLoading(false);
    }
  };

  // 升級 plan 卡片清單（不含 FREE，FREE 是自動掉回的狀態）
  const ORDER = ["STARTER", "GROWTH", "PRO", "FREE_TRIAL", "BRONZE", "SILVER", "GOLD"];
  const upgradablePlans = plans
    .filter((p) => p.code !== "FREE")
    .sort((a, b) => ORDER.indexOf(a.code) - ORDER.indexOf(b.code));

  // 確認 dialog
  const pendingPlanName = L[PLAN_NAME_KEY[pendingPlan?.code]] || pendingPlan?.code;
  const renderConfirmDialog = () => (
    <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
      <DialogTitle>{L.Content1 || "確認升級"}</DialogTitle>
      <DialogContent>
        <Typography>
          {L.Content2 || "您確定要升級至"} {pendingPlanName}（
          {pendingMethod === "extend"
            ? L.Content3 || "展延"
            : pendingMethod === "subscribe"
            ? L.Content4 || "訂閱月繳"
            : L.Content5 || "一次性年繳"}
          ）？
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setConfirmOpen(false)}>{L.Content6 || "取消"}</Button>
        <Button
          onClick={() => {
            setConfirmOpen(false);
            proceedUpgrade(pendingPlan.code, pendingMethod);
          }}
          autoFocus
          variant="contained"
          sx={{ fontWeight: "bold", color: COLORS.white }}
        >
          {L.Content7 || "確定升級"}
        </Button>
      </DialogActions>
    </Dialog>
  );

  return (
    <MDBox p={{ xs: 2, md: 3 }}>
      {/* 頁面標題 */}
      <MDTypography variant="h5" fontWeight="bold" mb={3}>
        {L.PageTitle || "我的方案"}
      </MDTypography>

      {/* === 上方 2 卡片：目前方案 + 方案時間 === */}
      <Grid container spacing={2} mb={3} alignItems="stretch">
        <Grid item xs={12} md={6}>
          <CurrentPlanCard
            myPlan={myPlan}
            L={L}
            onAutoRenewChanged={fetchMyPlan}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <PlanTimeCard myPlan={myPlan} L={L} />
        </Grid>
      </Grid>

      {/* === 中段：選擇方案 === */}
      <Card sx={{ p: { xs: 3, md: 4 }, borderRadius: 3, boxShadow: 1, mb: 4 }}>
        <MDTypography variant="h5" fontWeight="bold" textAlign="center" mb={2}>
          {L.ChooseSuitablePlan || "選擇適合的會員方案"}
        </MDTypography>

        {/* 月繳 / 年繳 toggle（共用 Tabs pill） */}
        <Box display="flex" justifyContent="center" mb={0.5}>
          <Tabs
            variant="pill"
            value={billingPeriod}
            onChange={(v) => setBillingPeriod(v)}
            tabs={[
              { value: "monthly", label: L.BillingMonthly || "月繳" },
              { value: "yearly", label: L.BillingYearly || "年繳" },
            ]}
          />
        </Box>
        <Typography variant="caption" sx={{ display: "block", textAlign: "center", color: COLORS.error, mb: 3 }}>
          {L.YearSaveHint || "(年方案省下約 17%)"}
        </Typography>

        {/* 3 張可升級方案卡 */}
        <Grid container spacing={3}>
          {upgradablePlans.map((plan) => (
            <UpgradePlanCard
              key={plan.code}
              plan={plan}
              myPlan={myPlan}
              billingPeriod={billingPeriod}
              L={L}
              onClickActivate={handleClickActivate}
            />
          ))}
        </Grid>
        {renderConfirmDialog()}
      </Card>

      {/* === 底部：功能比較表 === */}
      <Card sx={{ p: { xs: 2, md: 3 }, borderRadius: 3, boxShadow: 1 }}>
        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <Box sx={{ width: 4, height: 18, bgcolor: COLORS.main, borderRadius: 1 }} />
          <MDTypography sx={{ ...TYPO.title, color: COLORS.black }}>
            {L.FunctionCompareTitle || "功能比較表"}
          </MDTypography>
        </Box>
        <PlanCompareTable plans={plans} myPlan={myPlan} />
      </Card>
    </MDBox>
  );
}
